import { formatRating, toArabicNumerals } from "./arabicNumerals";

// The category header's numbers as copy. Lives outside the component so it can be unit-tested and
// so the component file keeps exporting only a component (react-refresh lint).

export type CategoryLedeInput = {
  storeCount: number;
  reviewCount: number;
  avgRating: number | null;
  lowRatingShare: number | null;
};

// A mean of one or two ratings is just those ratings, and a "share" of a handful of reviews is
// noise («١٠٠٪ من التجارب نجمتان أو أقل» over ONE review): the sentence drops the average below
// three reviews and the bar is hidden below ten.
export const MIN_REVIEWS_FOR_AVERAGE = 3;
export const MIN_REVIEWS_FOR_SHARE = 10;

// Grouped Arabic-Indic digits («١٬٥٤٩»), matching the other counts on the public pages.
const formatCount = (n: number) => n.toLocaleString("ar-EG");

// Arabic count agreement: 1 and 2 carry no numeral (singular / dual), 3-10 take the plural, 11+
// the singular again. Same rule as branchCountLabel. The duals are nominative, which is right
// here because both phrases are sentence subjects joined by «و», never governed by a preposition.
export const storesLabel = (n: number): string => {
  if (n === 1) return "متجر واحد";
  if (n === 2) return "متجران";
  if (n <= 10) return `${formatCount(n)} متاجر`;
  return `${formatCount(n)} متجر`;
};

export const experiencesCountLabel = (n: number): string => {
  if (n === 1) return "تجربة واحدة";
  if (n === 2) return "تجربتان";
  if (n <= 10) return `${formatCount(n)} تجارب`;
  return `${formatCount(n)} تجربة`;
};

export const experiencesLabel = (n: number): string => `${experiencesCountLabel(n)} من المشترين`;

export const ledeSentence = (i: CategoryLedeInput): string => {
  if (i.storeCount === 0) return "لا توجد متاجر منشورة بعد في هذه الفئة.";
  const stores = storesLabel(i.storeCount);
  if (i.reviewCount === 0) return `${stores}، ولا توجد تجارب منشورة بعد.`;
  const reviews = experiencesLabel(i.reviewCount);
  if (i.avgRating === null || i.reviewCount < MIN_REVIEWS_FOR_AVERAGE) return `${stores}، و${reviews}.`;
  return `${stores}، و${reviews}، متوسطها ${formatRating(i.avgRating)} من ${toArabicNumerals(5)}.`;
};

// The share of 1-2 star experiences as a bar: percent for the fill width, label for the text and
// the accessible name. null when the sample is too small for a share to mean anything.
export const lowRatingBar = (i: CategoryLedeInput): { percent: number; label: string } | null => {
  if (i.lowRatingShare === null || i.reviewCount < MIN_REVIEWS_FOR_SHARE) return null;
  const percent = Math.round(i.lowRatingShare * 100);
  return { percent, label: `${toArabicNumerals(percent)}٪ من التجارب نجمتان أو أقل` };
};
